import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, tokens } from '../api/client';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// Matches the website's country list. Value = ISO code sent as countryCode; "OTHER" reveals a text box.
const COUNTRIES = [
  ['IN', 'India (IN)'], ['SA', 'Saudi Arabia (SA)'], ['SG', 'Singapore (SG)'], ['HK', 'Hong Kong (HK)'],
  ['JP', 'Japan (JP)'], ['KR', 'South Korea (KR)'], ['TH', 'Thailand (TH)'], ['ID', 'Indonesia (ID)'],
  ['PH', 'Philippines (PH)'], ['VN', 'Vietnam (VN)'], ['MY', 'Malaysia (MY)'], ['ZA', 'South Africa (ZA)'],
  ['NG', 'Nigeria (NG)'], ['KE', 'Kenya (KE)'], ['EG', 'Egypt (EG)'], ['TR', 'Turkey (TR)'],
  ['IL', 'Israel (IL)'], ['NZ', 'New Zealand (NZ)'], ['OTHER', 'Other (specify below)'],
];

// Matches the website's document-type order. Values are exactly what the backend enum accepts.
const DOC_TYPES = [
  ['PASSPORT', 'Passport'], ['NATIONAL_ID', 'National ID card'], ['DRIVING_LICENSE', "Driver's license"],
  ['PAN', 'PAN card (India)'], ['AADHAAR', 'Aadhaar (India)'], ['OTHER', 'Other'],
];

type KycStatusResp = {
  status: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  approvedAt: string | null;
  canSubmit: boolean;
  submission: null | { rejectionReason: string | null; submittedAt: string };
};

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-3xl ${className}`} style={{ backgroundColor: '#15122e', border: '1px solid rgba(255,255,255,0.06)' }}>{children}</div>;
}

const inputStyle = { backgroundColor: '#1d1640', border: '1px solid rgba(255,255,255,0.08)', color: 'white' } as const;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm mb-1 block" style={{ color: '#c4b5fd' }}>{label}</label>
      {children}
      {hint && <p className="text-[11px] mt-1" style={{ color: '#8B82A8' }}>{hint}</p>}
    </div>
  );
}

function FilePick({ label, accept, file, onPick }: { label: string; accept: string; file: File | null; onPick: (f: File | null) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
      <button onClick={() => ref.current?.click()} className="w-full rounded-2xl px-4 py-4 text-left text-sm flex justify-between items-center" style={inputStyle}>
        <span style={{ color: file ? '#34D399' : '#c4b5fd' }}>{file ? `✓ ${file.name.slice(0, 24)}` : label}</span>
        <span style={{ color: '#8B82A8' }}>{file ? 'Change' : 'Choose file'}</span>
      </button>
    </>
  );
}

export default function Kyc() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ['kyc-status'], queryFn: () => api<KycStatusResp>('/api/kyc/status') });

  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [mobile, setMobile] = useState('');
  const [countryCode, setCountryCode] = useState('IN');
  const [countryOther, setCountryOther] = useState('');
  const [documentType, setDocumentType] = useState('PASSPORT');
  const [documentNumber, setDocumentNumber] = useState('');
  const [taxIdNumber, setTaxIdNumber] = useState('');
  const [doc, setDoc] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [taxDoc, setTaxDoc] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const s = statusQ.data;

  async function submit() {
    setErr(null);
    if (!fullName.trim() || !dob || !mobile.trim()) { setErr('Please fill in your name, date of birth and mobile number.'); return; }
    if (countryCode === 'OTHER' && !countryOther.trim()) { setErr('Please specify your country.'); return; }
    if (!doc || !selfie) { setErr('Please upload both your ID image and a selfie holding your ID.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('fullName', fullName.trim());
      fd.append('dateOfBirth', dob);
      fd.append('mobileNumber', mobile.trim());
      fd.append('countryCode', countryCode);
      if (countryCode === 'OTHER') fd.append('countryOther', countryOther.trim());
      fd.append('documentType', documentType);
      if (documentNumber.trim()) fd.append('documentNumber', documentNumber.trim());
      if (taxIdNumber.trim()) fd.append('taxIdNumber', taxIdNumber.trim());
      fd.append('document', doc);
      fd.append('selfie', selfie);
      if (taxDoc) fd.append('taxDocument', taxDoc);

      const res = await fetch(`${API_BASE}/api/kyc/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokens.getAccess()}` }, // no Content-Type — browser sets multipart boundary
        body: fd,
      });
      if (!res.ok) {
        let msg = `Submission failed (${res.status})`;
        try { const d = await res.json(); msg = d?.error?.message || d?.message || msg; } catch {}
        throw new Error(msg);
      }
      await qc.invalidateQueries({ queryKey: ['kyc-status'] });
      await qc.invalidateQueries({ queryKey: ['referral-me'] });
    } catch (e: any) {
      setErr(e?.message || 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const Header = (
    <div>
      <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>KYC VERIFICATION</div>
      <h1 className="text-3xl font-bold leading-tight" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Verify your identity</h1>
      <p className="text-sm mt-2" style={{ color: '#8B82A8' }}>KYC is required before commission payouts. Your documents are encrypted at rest and only accessible to authorised reviewers.</p>
    </div>
  );

  if (statusQ.isLoading) return <div className="space-y-5">{Header}<p className="text-sm" style={{ color: '#8B82A8' }}>Loading…</p></div>;

  if (s && s.status === 'APPROVED')
    return <div className="space-y-5">{Header}<Card className="p-6 text-center"><div className="text-5xl mb-2">✅</div><div className="text-xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Verified</div><p className="text-sm mt-1" style={{ color: '#8B82A8' }}>Your identity is approved. Commissions can now be paid out.</p></Card></div>;

  if (s && s.status === 'PENDING')
    return <div className="space-y-5">{Header}<Card className="p-6 text-center"><div className="text-5xl mb-2">⏳</div><div className="text-xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Under review</div><p className="text-sm mt-1" style={{ color: '#8B82A8' }}>Submitted. We typically review within 24–48 hours — payouts unlock automatically on approval.</p></Card></div>;

  return (
    <div className="space-y-5 pb-2">
      {Header}
      {s?.status === 'REJECTED' && s.submission?.rejectionReason && (
        <Card className="p-4"><div className="text-sm" style={{ color: '#F87171' }}>Previous submission rejected: {s.submission.rejectionReason}. Please resubmit.</div></Card>
      )}

      <Card className="p-5 space-y-4">
        <Field label="Full legal name">
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="As shown on your document" className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle} />
        </Field>

        <Field label="Date of birth">
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle} />
        </Field>

        <Field label="Mobile number" hint="Include your country code (e.g. +91 for India, +1 for US). We may contact you for verification.">
          <input value={mobile} onChange={(e) => setMobile(e.target.value)} inputMode="tel" placeholder="e.g. +91 98765 43210" className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle} />
        </Field>

        <Field label="Country">
          <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle}>
            {COUNTRIES.map(([v, l]) => <option key={v} value={v} style={{ color: 'black' }}>{l}</option>)}
          </select>
        </Field>

        {countryCode === 'OTHER' && (
          <Field label="Specify country">
            <input value={countryOther} onChange={(e) => setCountryOther(e.target.value)} placeholder="e.g. Pakistan, Sri Lanka, Bangladesh…" className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle} />
          </Field>
        )}

        <Field label="Document type">
          <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle}>
            {DOC_TYPES.map(([v, l]) => <option key={v} value={v} style={{ color: 'black' }}>{l}</option>)}
          </select>
        </Field>

        <Field label="Document number">
          <input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} placeholder="Number on your ID" className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle} />
        </Field>
      </Card>

      <Card className="p-5 space-y-4">
        <Field label="ID image (front)" hint="JPEG or PNG, max 5MB. Should be clear and readable.">
          <FilePick label="ID document photo" accept="image/jpeg,image/png" file={doc} onPick={setDoc} />
        </Field>
        <Field label="Selfie holding your ID" hint="Your face and the ID must both be clearly visible.">
          <FilePick label="Selfie with ID" accept="image/jpeg,image/png" file={selfie} onPick={setSelfie} />
        </Field>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>TAX INFORMATION (OPTIONAL)</div>
        <p className="text-[11px]" style={{ color: '#8B82A8' }}>For tax reporting in your jurisdiction (e.g. PAN, SSN, GST/VAT, TIN). Leave blank if not applicable.</p>
        <Field label="Tax ID number">
          <input value={taxIdNumber} onChange={(e) => setTaxIdNumber(e.target.value)} placeholder="e.g. ABCDE1234F (PAN), 123-45-6789 (SSN)…" className="w-full rounded-2xl px-4 py-3 outline-none" style={inputStyle} />
        </Field>
        <Field label="Tax ID document (optional)" hint="JPEG, PNG, or PDF, max 5MB. e.g. a scan of your PAN card.">
          <FilePick label="Tax document" accept="image/jpeg,image/png,application/pdf" file={taxDoc} onPick={setTaxDoc} />
        </Field>
      </Card>

      {err && <div className="rounded-2xl px-4 py-3 text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171' }}>{err}</div>}

      <button onClick={submit} disabled={busy} className="w-full rounded-2xl py-3.5 font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Submitting…' : 'Submit for review'}
      </button>

      <p className="text-center text-[11px]" style={{ color: '#52526E' }}>By submitting, you confirm the information is accurate and consent to its processing for identity verification.</p>
    </div>
  );
}